import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { FileText, Download, BarChart3, Calendar, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientReports({ client, getHeaders }) {
  const { t } = useTranslation('dashboard');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetchReports = () => {
    setLoading(true);
    fetch(`${API}/api/client/reports`, { headers: getHeaders() })
      .then(r => r.ok ? r.json() : { reports: [] })
      .then(d => setReports(d.reports || []))
      .catch(() => setReports([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchReports(); }, [getHeaders]);

  const generateReport = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/client/reports/generate`, {
        method: 'POST', headers: getHeaders(),
      });
      if (res.ok) { fetchReports(); }
      else { const d = await res.json(); alert(d.detail || t('reports.failedGenerate')); }
    } catch (e) { console.error(e); }
    finally { setGenerating(false); }
  };

  const downloadReport = (reportId) => {
    const token = localStorage.getItem('client_token');
    window.open(`${API}/api/client/reports/download/${reportId}?token=${token}`, '_blank');
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-400">{t('reports.loading')}</div>;
  }

  return (
    <div className="space-y-4" data-testid="client-reports">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-400" />
          <h2 className="text-lg font-semibold text-gray-900">{t('reports.title')}</h2>
        </div>
        <Button
          size="sm"
          onClick={generateReport}
          disabled={generating}
          className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
          data-testid="generate-report-btn"
        >
          <RefreshCw className={`w-3 h-3 mr-1.5 ${generating ? 'animate-spin' : ''}`} />
          {generating ? t('reports.generating') : t('reports.generateReport')}
        </Button>
      </div>

      {reports.length === 0 ? (
        <Card className="bg-white border-gray-200">
          <CardContent className="p-8 text-center">
            <FileText className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">{t('reports.noReportsTitle')}</p>
            <p className="text-sm text-gray-500 mt-1">{t('reports.noReportsSub')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {reports.map(report => (
            <Card key={report.report_id} className="bg-white border-gray-200" data-testid={`client-report-${report.report_id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-4 h-4 text-blue-400" />
                      <span className="text-sm font-medium text-gray-900">{report.period_label}</span>
                    </div>
                    <div className="flex gap-4 text-xs text-gray-500 mt-1">
                      {report.stats && (
                        <>
                          <Badge variant="outline" className="text-[10px] border-gray-200">
                            {t('reports.postsLabel', { count: report.stats.total_posts || 0 })}
                          </Badge>
                          <Badge variant="outline" className="text-[10px] border-gray-200">
                            {t('reports.seoLabel', { pct: report.stats.seo_progress_pct || 0 })}
                          </Badge>
                          <Badge variant="outline" className="text-[10px] border-gray-200">
                            {t('reports.leadsLabel', { count: report.stats.total_leads || 0 })}
                          </Badge>
                        </>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      <Calendar className="w-3 h-3 inline mr-1" />
                      {t('reports.generatedOn', { date: report.generated_at ? new Date(report.generated_at).toLocaleDateString() : t('reports.generatedNa') })}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => downloadReport(report.report_id)}
                    data-testid={`client-download-${report.report_id}`}
                  >
                    <Download className="w-3 h-3 mr-1" /> {t('reports.downloadPdf')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
